/* eslint no-param-reassign: ["error", { "props": false }] */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "app/store";
import { getInvitationPaymentMultiple } from "api/bookings";
import { FeedItem, getDashboardFeed } from "api/feed";

// home state
interface HomeState {
    feedItems: FeedItem[],
    error: string | null,
    isLoading: boolean,
    offset: number,
}

// initial state
const initialState: HomeState = {
    feedItems: [],
    error: null,
    isLoading: false,
    offset: 0,
};

// generic reducers
const startLoading = (state: HomeState) => {
    state.isLoading = true;
    state.error = null;
};
const loadingFailed = (state: HomeState, { payload }: PayloadAction<string>) => {
    state.isLoading = false;
    state.error = payload;
};

// slice
const homeSlice = createSlice({
    name: "home",
    initialState,
    reducers: {
        getFeedStart: startLoading,
        getFeedSuccess: (state, { payload }: PayloadAction<{ items: FeedItem[], offset: number }>) => {
            state.isLoading = false;
            state.feedItems = state.feedItems.slice(0, payload.offset).concat(payload.items);
            state.offset = payload.offset + payload.items.length;
        },
        getFeedFailure: loadingFailed,
    },
});

// inferred actions
const { getFeedStart, getFeedSuccess, getFeedFailure } = homeSlice.actions;

// fetch dashboard feed in a club
export const fetchDashboardFeed = (clubId: string, offset: number): AppThunk => async (dispatch) => {
    try {
        dispatch(getFeedStart());
        const feedItems = await getDashboardFeed({ clubId, offset, limit: 20 });

        // fetch additional payment info
        const needPaymentInfo = feedItems.filter((i) => i.hasPaymentInfo).map((i) => i.booking!.id);
        if (needPaymentInfo.length > 0) {
            const paymentInfos = await getInvitationPaymentMultiple({ bookingIds: needPaymentInfo });
            Object.entries(paymentInfos).forEach(([id, info]) => { feedItems.find((i) => i.booking?.id === id)!.paymentInfo = info; });
        }

        // sort
        feedItems.sort((i1, i2) => i2.createdAt.localeCompare(i1.createdAt));

        dispatch(getFeedSuccess({ items: feedItems, offset }));
    } catch (error: any) {
        dispatch(getFeedFailure(error.type));
    }
};

export default homeSlice.reducer;
